import React from 'react';
import { Modal } from '../../common/Modal';
import { FileText, Calendar, File, Image as ImageIcon } from 'lucide-react';
import { Evidence } from '../../../types/pilgrimGuide';
import { useLanguage } from '../../../contexts/LanguageContext';
import { motion } from 'framer-motion';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  guideName: string;
  evidence?: Evidence;
};

export const EvidenceViewModal = ({
  isOpen,
  onClose,
  guideName,
  evidence,
}: Props) => {
  const { translations } = useLanguage();

  if (!evidence) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`${translations?.evidenceViewModal?.title || 'Guide Evidence'} - ${guideName}`}
      >
        <div className="text-center py-8 text-gray-500">
          {translations?.evidenceViewModal?.noEvidence ||
            'No evidence has been submitted yet'}
        </div>
      </Modal>
    );
  }

  // Handle legacy format (single image URL)
  const hasLegacyImage = 'imageUrl' in evidence && evidence.imageUrl;
  const hasNewFiles = 'files' in evidence && evidence.files && evidence.files.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${translations?.evidenceViewModal?.title || 'Guide Evidence'} - ${guideName}`}
    >
      <div className="space-y-6">
        {/* Text Evidence */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <FileText size={16} className="text-primary-500" />
            <span>{translations?.evidenceViewModal?.textEvidence || 'Text Evidence'}</span>
          </div>
          <p className="text-gray-600 bg-gray-50 p-4 rounded-lg border">
            {evidence.text}
          </p>
        </div>

        {/* Legacy Image Evidence */}
        {hasLegacyImage && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <ImageIcon size={16} className="text-primary-500" />
              <span>{translations?.evidenceViewModal?.imageEvidence || 'Attached Image'}</span>
            </div>
            <motion.a
              href={evidence.imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="block"
            >
              <img
                src={evidence.imageUrl}
                alt="Evidence"
                className="rounded-lg max-w-full h-auto border"
              />
            </motion.a>
          </div>
        )}

        {/* New Files Evidence */}
        {hasNewFiles && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <File size={16} className="text-primary-500" />
              <span>{translations?.evidenceViewModal?.filesEvidence || 'Attached Files'}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {evidence.files.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group"
                >
                  {file.type === 'image' ? (
                    <div className="relative">
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={file.url}
                          alt={file.name}
                          className="rounded-lg w-full h-48 object-cover border group-hover:opacity-90 transition-opacity"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg">
                            {translations?.evidenceViewModal?.viewImage || 'View Image'}
                          </div>
                        </div>
                      </a>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 rounded-b-lg">
                        <p className="text-sm truncate">{file.name}</p>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <File size={24} className="text-red-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-sm text-gray-500">PDF Document</p>
                      </div>
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="flex flex-col gap-2 text-sm text-gray-500 border-t pt-4">
          <div className="flex items-center gap-2">
            <Calendar size={14} />
            <span>
              {translations?.evidenceViewModal?.submittedAt || 'Submission Date'}:{' '}
              {new Date(evidence.submittedAt).toLocaleDateString('en-US')}
            </span>
          </div>
          {evidence.updatedAt > evidence.submittedAt && (
            <div className="flex items-center gap-2">
              <Calendar size={14} />
              <span>
                {translations?.evidenceViewModal?.updatedAt || 'Last Updated'}:{' '}
                {new Date(evidence.updatedAt).toLocaleDateString('en-US')}
              </span>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};